import { useDrag } from '@use-gesture/react';
import { useEffect, useRef, useState } from 'react';

import { useChartLayoutRatio } from './useChartLayoutRatio.js';
import type { Orientation } from '../../../../../core/types/orientation.js';
import type {
  AreasSizes,
  UseResizableLayoutResponse,
} from '../types/chart-layout-internal.js';
import {
  getAreasSizeOnDragHandlers,
  getDefaultAreasSize,
} from '../utils/get-areas-size-on-drag.js';
import { getAreasSizeHandlers } from '../utils/get-areas-size.js';
import { getInitialDimensions } from '../utils/get-initial-dimensions.js';
import { getResizerWrapperSize } from '../utils/get-resizer-wrapper-size.js';
import { isVertical as isVerticalFn } from '../utils/orientation.js';

const initialDimensions = {
  width: 0,
  height: 0,
};

/*
 * Method to calculate the size of the referenced element whilst dragging the
 * referenced line resizer.
 *
 */
export const useResizableLayout = (
  containerWidth: number,
  containerHeight: number,
  orientation: Orientation,
  showResizer: boolean = true,
  showLegend: boolean = true,
  onRatioChange?: (ratio: number) => void,
): UseResizableLayoutResponse => {
  const legendResizeContainerRef = useRef<HTMLDivElement | null>(null);
  const legendResizeLineRef = useRef<HTMLDivElement | null>(null);
  const legendContainerRef = useRef<HTMLDivElement | null>(null);

  const { ratio, ratioBoundaries, overrideRatio, legendCandidateSize } =
    useChartLayoutRatio();

  const { minWidth, maxHeight, maxWidth, minHeight } = ratioBoundaries;

  const [areasSizes, setAreasSizes] = useState<AreasSizes>({
    legend: initialDimensions,
    graph: initialDimensions,
  });

  const isVertical = isVerticalFn(orientation);

  const resizerWrapperSize = showResizer
    ? getResizerWrapperSize(isVertical, legendResizeContainerRef)
    : 0;

  const width = isVertical
    ? containerWidth
    : containerWidth - resizerWrapperSize;

  const height = isVertical
    ? containerHeight - resizerWrapperSize
    : containerHeight;

  const { height: initialHeight, width: initialWidth } = showLegend
    ? getInitialDimensions(
        orientation,
        width,
        height,
        containerWidth,
        containerHeight,
        ratio,
        ratioBoundaries,
        legendCandidateSize,
        overrideRatio,
      )
    : initialDimensions;

  useEffect(() => {
    const { getAreasHeightSize, getAreasWidthSize } = getAreasSizeHandlers(
      containerHeight,
      containerWidth,
      resizerWrapperSize,
    );

    if (!isVertical && initialWidth && containerWidth) {
      const areasSizes = getAreasWidthSize(maxWidth, initialWidth);
      setAreasSizes(areasSizes);
    } else if (isVertical && initialHeight && containerHeight) {
      const areasSizes = getAreasHeightSize(maxHeight, initialHeight);
      setAreasSizes(areasSizes);
    } else {
      const onlyGraphArea = {
        legend: initialDimensions,
        graph: { width: containerWidth, height: containerHeight },
      };
      setAreasSizes(onlyGraphArea);
    }
  }, [
    containerHeight,
    containerWidth,
    initialHeight,
    initialWidth,
    isVertical,
    maxHeight,
    maxWidth,
    resizerWrapperSize,
  ]);

  useDrag(
    (dragEvent) => {
      const [horizontalDraggedAmount, verticalDraggedAmount] =
        dragEvent['delta'];

      const currentLegendContainer = legendContainerRef.current;
      if (!currentLegendContainer) {
        return;
      }

      const { getAreasHeightSizeOnDrag, getAreasWidthSizeOnDrag } =
        getAreasSizeOnDragHandlers(
          containerHeight,
          containerWidth,
          currentLegendContainer,
          resizerWrapperSize,
        );

      const newAreasSizes = isVertical
        ? getAreasHeightSizeOnDrag(minHeight, maxHeight, verticalDraggedAmount)
        : getAreasWidthSizeOnDrag(minWidth, maxWidth, horizontalDraggedAmount);

      setAreasSizes((prevAreasSizes) => {
        if (newAreasSizes) {
          return newAreasSizes;
        }

        const draggedAmount = isVertical
          ? verticalDraggedAmount
          : horizontalDraggedAmount;

        const containerSize = isVertical ? containerHeight : containerWidth;

        return getDefaultAreasSize(
          prevAreasSizes,
          draggedAmount,
          containerSize,
          resizerWrapperSize,
          orientation,
        );
      });

      const legendPercentage = isVertical
        ? (areasSizes.legend.height * 100) / containerHeight
        : (areasSizes.legend.width * 100) / containerWidth;

      onRatioChange?.(Math.round(legendPercentage));
    },
    {
      target: legendResizeLineRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true },
      enabled: showLegend && showResizer,
    },
  );

  return {
    legendWidth: areasSizes.legend.width,
    legendHeight: areasSizes.legend.height,
    graphSize: areasSizes.graph,
    legendResizeLineRef,
    legendContainerRef,
    legendResizeContainerRef,
  };
};
